import { AdminReviewData, BroadcastCredentials, LivestreamingApiClient, LivestreamingApiError, Session, Streamer } from "./lib/api";
import { BandLabAuthSession, BandLabAuthStore } from "./lib/auth";
import { isWebBroadcastSupported, MediaDeviceOption, WebBroadcastController } from "./lib/broadcast";
import { attachPlayback } from "./lib/player";
import { formatDateTime, formatDuration, formatStatus } from "./lib/format";

interface LivestreamingAppDependencies {
    readonly apiClient: LivestreamingApiClient;
    readonly authStore: BandLabAuthStore;
    readonly root: HTMLElement;
}

interface LivestreamingAppState {
    readonly authSession: BandLabAuthSession | null;
    readonly authCallbackProcessing: boolean;
    readonly authCallbackError: string | null;
    readonly apiHealthy: boolean;
    readonly loading: boolean;
    readonly errorMessage: string | null;
    readonly streamers: Streamer[];
    readonly ownedStreamerId: string | null;
    readonly selectedStreamerId: string | null;
    readonly selectedStreamerSessions: Session[];
    readonly liveSessions: Session[];
    readonly selectedPlaybackUrl: string | null;
    readonly selectedPlaybackSeekSeconds: number | null;
    readonly lastRefreshedAt: string | null;
    readonly adminReview: AdminReviewData | null;
    readonly revealedBroadcastCredentials: BroadcastCredentials | null;
    readonly webBroadcastSupported: boolean;
    readonly webBroadcastPreparing: boolean;
    readonly webBroadcastStarting: boolean;
    readonly webBroadcastActive: boolean;
    readonly webBroadcastEnding: boolean;
    readonly webBroadcastLiveSessionObserved: boolean;
    readonly webBroadcastErrorMessage: string | null;
    readonly webBroadcastConnectionState: string | null;
    readonly webBroadcastVideoDevices: MediaDeviceOption[];
    readonly webBroadcastAudioDevices: MediaDeviceOption[];
    readonly selectedWebBroadcastVideoDeviceId: string | null;
    readonly selectedWebBroadcastAudioDeviceId: string | null;
}

export class LivestreamingApp {
    private readonly apiClient: LivestreamingApiClient;
    private readonly authStore: BandLabAuthStore;
    private readonly root: HTMLElement;
    private readonly selectedStreamerStorageKey = "livestreaming-ui-selected-streamer";
    private state: LivestreamingAppState;
    private detachPlayer: (() => void) | null = null;
    private readonly webBroadcastController = new WebBroadcastController();
    private startupPollingHandle: number | null = null;
    private dashboardPollingHandle: number | null = null;
    private broadcastCountdownHandle: number | null = null;
    private broadcastBlockRefreshInFlight = false;

    constructor(dependencies: LivestreamingAppDependencies) {
        this.apiClient = dependencies.apiClient;
        this.authStore = dependencies.authStore;
        this.root = dependencies.root;
        this.state = {
            authSession: this.authStore.load(),
            authCallbackProcessing: this.authStore.isCallbackRoute(),
            authCallbackError: null,
            apiHealthy: true,
            loading: false,
            errorMessage: null,
            streamers: [],
            ownedStreamerId: null,
            selectedStreamerId: window.localStorage.getItem(this.selectedStreamerStorageKey),
            selectedStreamerSessions: [],
            liveSessions: [],
            selectedPlaybackUrl: null,
            selectedPlaybackSeekSeconds: null,
            lastRefreshedAt: null,
            adminReview: null,
            revealedBroadcastCredentials: null,
            webBroadcastSupported: isWebBroadcastSupported(),
            webBroadcastPreparing: false,
            webBroadcastStarting: false,
            webBroadcastActive: false,
            webBroadcastEnding: false,
            webBroadcastLiveSessionObserved: false,
            webBroadcastErrorMessage: null,
            webBroadcastConnectionState: null,
            webBroadcastVideoDevices: [],
            webBroadcastAudioDevices: [],
            selectedWebBroadcastVideoDeviceId: null,
            selectedWebBroadcastAudioDeviceId: null,
        };
    }

    public async initialize(): Promise<void> {
        this.render();

        if (this.authStore.isCallbackRoute()) {
            await this.completeBandLabAuthCallback();
            return;
        }

        if (this.state.authSession) {
            await this.bootstrapAuthenticatedSession();
        }
    }

    private async refreshDashboardData(options: { forceRender?: boolean; silent?: boolean; selectedPlaybackSeekSeconds?: number | null; selectedStreamerId?: string | null; } = {}): Promise<void> {
        const silent = options.silent ?? false;
        const forceRender = options.forceRender ?? false;
        if (!this.state.authSession) {
            return;
        }

        this.updateState({
            loading: !silent,
            errorMessage: silent ? this.state.errorMessage : null,
        }, {
            render: !silent,
        });

        try {
            const [health, streamers, liveSessions, ownedStreamer] = await Promise.all([
                this.apiClient.getHealth(),
                this.apiClient.listStreamers(),
                this.apiClient.listLiveSessions(),
                this.apiClient.getMyStreamer(),
            ]);
            const adminReview = ownedStreamer.admin ? await this.apiClient.getAdminReview() : null;
            const mergedStreamers = mergeOwnedStreamer(streamers, ownedStreamer);

            const selectedStreamerId = resolveSelectedStreamerId(
                mergedStreamers,
                options.selectedStreamerId ?? this.state.selectedStreamerId,
                ownedStreamer.streamerId,
            );
            const selectedStreamerSessions = selectedStreamerId
                ? await this.apiClient.getStreamerSessions(selectedStreamerId)
                : [];
            const selectedPlaybackUrl = resolvePlaybackUrl({
                existing: options.selectedStreamerId !== undefined ? null : this.state.selectedPlaybackUrl,
                liveSessions,
                streamerSessions: selectedStreamerSessions,
                allowGlobalLiveFallback: !selectedStreamerId,
            });
            const broadcastReconciliation = resolveWebBroadcastSessionReconciliation({
                webBroadcastActive: this.state.webBroadcastActive,
                webBroadcastStarting: this.state.webBroadcastStarting,
                liveSessionObserved: this.state.webBroadcastLiveSessionObserved,
                ownedStreamerId: ownedStreamer.streamerId,
                liveSessions,
            });
            const ownedBroadcastBlockState = resolveBroadcastBlockState(ownedStreamer);

            if (broadcastReconciliation.stoppedByBackend) {
                this.stopBroadcastStartupPolling();
                this.webBroadcastController.stop();
            }

            const nextState: Partial<LivestreamingAppState> = {
                apiHealthy: health.status === "ok" && health.service === "livestreaming",
                loading: false,
                errorMessage: null,
                streamers: mergedStreamers,
                ownedStreamerId: ownedStreamer.streamerId,
                selectedStreamerId,
                selectedStreamerSessions,
                liveSessions,
                selectedPlaybackUrl,
                selectedPlaybackSeekSeconds: options.selectedPlaybackSeekSeconds !== undefined
                    ? options.selectedPlaybackSeekSeconds
                    : options.selectedStreamerId !== undefined
                        ? null
                        : this.state.selectedPlaybackSeekSeconds,
                lastRefreshedAt: new Date().toISOString(),
                adminReview,
                revealedBroadcastCredentials: canRevealObsCredentialsForStreamer(ownedStreamer, this.state.webBroadcastEnding)
                    && this.state.revealedBroadcastCredentials?.streamerId === ownedStreamer.streamerId
                    ? this.state.revealedBroadcastCredentials
                    : null,
                webBroadcastEnding: this.state.webBroadcastEnding && ownedBroadcastBlockState.reason === "active_session",
                webBroadcastLiveSessionObserved: broadcastReconciliation.nextLiveSessionObserved,
                ...(broadcastReconciliation.stoppedByBackend
                    ? {
                        webBroadcastActive: false,
                        webBroadcastStarting: false,
                        webBroadcastConnectionState: "disconnected",
                    }
                    : {}),
            };

            const keepPlaybackStable = silent && !forceRender && !broadcastReconciliation.stoppedByBackend && this.shouldAvoidFullDashboardRerender();
            this.updateState(nextState, {
                render: forceRender || (!silent && !keepPlaybackStable),
            });

            if (keepPlaybackStable) {
                this.refreshPassiveDashboardUi();
            }
        } catch (error) {
            if (this.handleUnauthorizedError(error)) {
                return;
            }

            this.updateState({
                loading: false,
                errorMessage: getErrorMessage(error),
            }, {
                render: forceRender || !silent,
            });

            if (silent) {
                this.refreshPassiveDashboardUi();
            }
        }
    }

    private async bootstrapAuthenticatedSession(): Promise<void> {
        if (!this.state.authSession) {
            return;
        }

        try {
            this.updateState({
                loading: true,
                errorMessage: null,
            });

            try {
                await this.apiClient.getMyStreamer();
            } catch (error) {
                if (!(error instanceof LivestreamingApiError) || error.statusCode !== 404) {
                    throw error;
                }

                await this.apiClient.ensureMyStreamer({
                    handle: this.state.authSession.handle,
                    displayName: this.state.authSession.displayName,
                });
            }

            await this.refreshDashboardData();
            this.startDashboardPolling();
        } catch (error) {
            if (this.handleUnauthorizedError(error)) {
                return;
            }

            this.updateState({
                loading: false,
                errorMessage: getErrorMessage(error),
            });
        }
    }

    private async completeBandLabAuthCallback(): Promise<void> {
        this.updateState({
            authCallbackProcessing: true,
            authCallbackError: null,
        });

        const callbackResult = await this.authStore.handleCallback();
        if (!callbackResult.success || !callbackResult.session) {
            this.updateState({
                authCallbackProcessing: false,
                authCallbackError: callbackResult.error ?? "BandLab sign-in failed",
            });
            return;
        }

        window.history.replaceState({}, "", callbackResult.redirectPath ?? "/");
        this.updateState({
            authSession: callbackResult.session,
            authCallbackProcessing: false,
            authCallbackError: null,
        });

        await this.bootstrapAuthenticatedSession();
    }

    private updateState(partialState: Partial<LivestreamingAppState>, options: { render?: boolean; } = {}): void {
        this.state = {
            ...this.state,
            ...partialState,
        };

        if (partialState.selectedStreamerId !== undefined) {
            if (partialState.selectedStreamerId) {
                window.localStorage.setItem(this.selectedStreamerStorageKey, partialState.selectedStreamerId);
            } else {
                window.localStorage.removeItem(this.selectedStreamerStorageKey);
            }
        }

        if (options.render ?? true) {
            this.render();
        }

        this.reconcileBroadcastCountdownTicker();
    }

    private render(): void {
        this.root.innerHTML = this.state.authSession
            ? this.renderDashboard()
            : this.renderLogin();

        this.bindEvents();
        this.bindPlayer();
        this.bindBroadcastStudio();
    }

    private renderLogin(): string {
        if (this.state.authCallbackProcessing) {
            return `
                <div class="page-shell login-shell">
                    <div class="background-grid" aria-hidden="true"></div>
                    <main class="login-stage">
                        <section class="login-card">
                            <div class="login-card-header">
                                <span class="login-step">BandLab Accounts</span>
                                <h2>Signing you in…</h2>
                                <p>We are completing your BandLab login and preparing your livestreaming streamer.</p>
                            </div>
                        </section>
                    </main>
                </div>
            `;
        }

        return `
            <div class="page-shell login-shell">
                <div class="background-grid" aria-hidden="true"></div>
                <header class="brand-row">
                    <div class="brand-mark">${renderBrand()}</div>
                    <div class="sandbox-pill">internal sandbox</div>
                </header>
                <main class="login-stage">
                    <section class="hero-copy">
                        <p class="eyebrow">BandLab Livestreaming</p>
                        <h1>Start, monitor, and review live streams from one internal control room.</h1>
                        <p class="lede">
                            This sandbox is for the dev-bandlab livestreaming proof of concept. Use it to provision
                            stream credentials, watch live sessions, and review recordings while we shape the product.
                        </p>
                    </section>
                    <section class="login-card">
                        <div class="login-card-header">
                            <span class="login-step">BandLab Accounts</span>
                            <h2>Sign in to continue</h2>
                            <p>
                                Sign in with BandLab to open your own streamer controls and use bearer-authenticated
                                API access throughout the sandbox.
                            </p>
                        </div>
                        ${this.state.authCallbackError ? `<div class="inline-error">${escapeHtml(this.state.authCallbackError)}</div>` : ""}
                        <div class="login-form">
                            <button type="button" class="login-button" data-action="sign-in">Continue with BandLab</button>
                        </div>
                        <p class="login-note">
                            The first time you sign in we will attach one livestreaming streamer to your BandLab user id.
                        </p>
                    </section>
                </main>
            </div>
        `;
    }

    private renderDashboard(): string {
        const ownedStreamer = this.state.streamers.find((streamer) => streamer.streamerId === this.state.ownedStreamerId) ?? null;
        const selectedStreamer = this.state.streamers.find((streamer) => streamer.streamerId === this.state.selectedStreamerId) ?? null;
        const recordingSessions = this.state.selectedStreamerSessions.filter((session) => session.playbackKind === "recording");
        const selectedPlaybackSession = resolveSelectedPlaybackSession(selectedStreamer, this.state.selectedStreamerSessions, this.state.selectedPlaybackUrl);
        const orderedStreamers = sortStreamers(this.state.streamers, this.state.liveSessions);

        return `
            <div class="page-shell dashboard-shell">
                <div class="background-grid" aria-hidden="true"></div>
                <header class="topbar">
                    <div class="brand-mark">${renderBrand()}</div>
                    <div class="topbar-meta">
                        <div class="topbar-actions">
                            <div class="status-chip ${this.state.apiHealthy ? "healthy" : "warning"}" data-role="api-health-chip">
                                ${this.state.apiHealthy ? "API healthy" : "API unavailable"}
                            </div>
                            <button type="button" class="ghost-button topbar-button" data-action="refresh-dashboard">Refresh</button>
                            ${renderUserChip(this.state.authSession)}
                            <button type="button" class="ghost-button topbar-button" data-action="sign-out">Sign out</button>
                        </div>
                    </div>
                </header>

                <main class="dashboard-layout">
                    <section class="hero-panel">
                        <div>
                            <p class="eyebrow">Livestreaming Control Room</p>
                            <h1>Stream as yourself, track live sessions, and review recordings.</h1>
                        </div>
                        <div class="hero-stats">
                            <div class="stat-card">
                                <span>Streamers</span>
                                <strong data-role="streamers-count">${this.state.streamers.length}</strong>
                            </div>
                            <div class="stat-card">
                                <span>Live now</span>
                                <strong data-role="live-count">${this.state.liveSessions.length}</strong>
                            </div>
                            <div class="stat-card">
                                <span>Recordings</span>
                                <strong data-role="recordings-count">${recordingSessions.length}</strong>
                            </div>
                            <div class="stat-card">
                                <span>Last refresh</span>
                                <strong data-role="last-refresh">${escapeHtml(formatDateTime(this.state.lastRefreshedAt))}</strong>
                            </div>
                        </div>
                    </section>

                    ${this.state.errorMessage ? `<section class="error-banner">${escapeHtml(this.state.errorMessage)}</section>` : ""}
                    ${ownedStreamer?.moderationStatus === "blocked" && !ownedStreamer.admin ? this.renderBlockedAccountNotice() : ""}

                    <div class="dashboard-grid">
                        <section class="panel wide-panel watch-panel">
                            <div class="watch-layout">
                                <div class="watch-main">
                                    <div class="panel-heading">
                                        <div>
                                            <p class="eyebrow">Watch</p>
                                            <h2>Live preview</h2>
                                        </div>
                                        ${selectedPlaybackSession ? `<span class="status-pill ${selectedPlaybackSession.isLive ? "live" : ""}">${escapeHtml(formatStatus(selectedPlaybackSession.status))}</span>` : ""}
                                    </div>
                                    <div class="player-card">
                                        <video data-role="player" controls playsinline></video>
                                        <div class="player-toolbar-row">
                                            <div data-role="player-controls"></div>
                                            <div data-role="quality-controls"></div>
                                        </div>
                                        <div class="player-meta">
                                            ${selectedPlaybackSession ? `
                                                <h3>${escapeHtml(resolvePlaybackTitle(selectedPlaybackSession, selectedStreamer, this.state.streamers))}</h3>
                                                <p>${escapeHtml(resolvePlaybackSubtitle(selectedPlaybackSession))}</p>
                                            ` : `
                                                <h3>No playback selected</h3>
                                                <p>Pick a live session or an available recording to preview it here.</p>
                                            `}
                                        </div>
                                    </div>
                                </div>

                                <aside class="watch-rail">
                                    <div class="panel-heading">
                                        <div>
                                            <p class="eyebrow">Library</p>
                                            <h2>Sessions and recordings</h2>
                                        </div>
                                    </div>
                                    <div class="watch-list">
                                        ${selectedStreamer
        ? this.state.selectedStreamerSessions.length
            ? this.state.selectedStreamerSessions.map((session) => `
                                                    <article class="watch-list-item ${resolveSessionPlaybackUrl(session) === this.state.selectedPlaybackUrl ? "selected" : ""} ${session.canPlay ? "playable" : ""}">
                                                        <button
                                                            type="button"
                                                            class="watch-list-select"
                                                            data-action="watch-playback"
                                                            data-playback-url="${escapeHtmlAttribute(resolveSessionPlaybackUrl(session) ?? "")}"
                                                            ${session.canPlay ? "" : "disabled"}
                                                        >
                                                            ${session.recordingThumbnailUrl ? `
                                                                <img
                                                                    class="watch-list-thumbnail"
                                                                    src="${escapeHtmlAttribute(session.recordingThumbnailUrl)}"
                                                                    alt="Recording thumbnail for ${escapeHtmlAttribute(session.sessionId)}"
                                                                />
                                                            ` : `<div class="watch-list-thumbnail placeholder"></div>`}
                                                            <div class="watch-list-copy">
                                                                <div class="watch-list-title-row">
                                                                    <h3>${escapeHtml(session.sessionId)}</h3>
                                                                </div>
                                                                <p>${escapeHtml(formatDateTime(session.startedAt))}</p>
                                                                <p>${escapeHtml(resolveSessionCardSubtitle(session))}</p>
                                                            </div>
                                                        </button>
                                                        <div class="watch-list-status-column">
                                                            <span class="status-pill ${session.isLive ? "live" : ""}">${escapeHtml(formatStatus(session.status))}</span>
                                                            ${canDeleteOwnedSession(session, this.state.ownedStreamerId) ? `
                                                                <button
                                                                    type="button"
                                                                    class="ghost-button danger rail-delete-button"
                                                                    data-action="delete-session"
                                                                    data-session-id="${session.sessionId}"
                                                                >
                                                                    DELETE
                                                                </button>
                                                            ` : ""}
                                                        </div>
                                                    </article>
                                                `).join("")
            : `<div class="empty-card">This streamer has no sessions yet.</div>`
        : `<div class="empty-card">Choose a streamer to inspect its live history and recordings.</div>`}
                                    </div>
                                </aside>
                            </div>
                        </section>

                        <section class="panel wide-panel operator-panel">
                            <div class="operator-layout">
                                <div class="operator-controls">
                                    ${this.renderStreamerControls(ownedStreamer)}
                                </div>

                                <aside class="operator-roster">
                                    <div class="panel-heading">
                                        <div>
                                            <p class="eyebrow">Roster</p>
                                            <h2>Available streamers</h2>
                                        </div>
                                    </div>
                                    <div data-role="roster-list">
                                        ${this.renderRosterList(orderedStreamers)}
                                    </div>
                                </aside>
                            </div>
                        </section>

                        ${ownedStreamer?.admin ? this.renderAdminReviewPanel() : ""}
                    </div>
                </main>
            </div>
        `;
    }

    private renderStreamerControls(ownedStreamer: Streamer | null): string {
        if (!ownedStreamer) {
            return `
                <div class="panel-heading compact-heading">
                    <div>
                        <p class="eyebrow">Streamer controls</p>
                        <h2>Provisioning your streamer</h2>
                    </div>
                    ${this.state.loading ? `<span class="loading-pill">Syncing…</span>` : ""}
                </div>
                <div class="empty-card">We are preparing your BandLab-linked streamer. This should only take a moment.</div>
            `;
        }

        const canRevealObsCredentials = canRevealObsCredentialsForStreamer(ownedStreamer, this.state.webBroadcastEnding);
        const revealedCredentials = canRevealObsCredentials && this.state.revealedBroadcastCredentials?.streamerId === ownedStreamer.streamerId
            ? this.state.revealedBroadcastCredentials
            : null;
        const moderationNotice = renderStreamerModerationNotice(ownedStreamer);
        const broadcastBlockState = resolveEffectiveBroadcastBlockState(ownedStreamer, this.state.webBroadcastEnding);
        const membershipAccessMessage = resolveMembershipAccessMessage(ownedStreamer);
        return `
            <div class="panel-heading compact-heading">
                <div class="selected-streamer-summary">
                    <p class="eyebrow">Streamer controls</p>
                    <h2>${escapeHtml(ownedStreamer.displayName)}</h2>
                    <p class="form-hint">@${escapeHtml(ownedStreamer.handle)} • Max session ${formatDuration(ownedStreamer.maxSessionDurationSeconds)}</p>
                </div>
                <div class="control-panel-actions">
                    ${this.state.webBroadcastConnectionState ? `<span class="status-pill ${this.state.webBroadcastConnectionState === "disconnected" ? "" : "accent"}">${escapeHtml(formatStatus(this.state.webBroadcastConnectionState))}</span>` : ""}
                </div>
            </div>
            ${moderationNotice ? `<div class="inline-warning">${escapeHtml(moderationNotice)}</div>` : ""}
            ${broadcastBlockState.message ? `<div class="inline-warning" data-role="broadcast-block-message">${escapeHtml(broadcastBlockState.message)}</div>` : ""}
            ${membershipAccessMessage ? `<div class="inline-warning">${escapeHtml(membershipAccessMessage)}</div>` : ""}
            <div class="streamer-control-grid">
                <section class="control-card">
                    <div class="panel-heading compact-heading">
                        <div>
                            <p class="eyebrow">Browser</p>
                            <h3>Stream from browser</h3>
                        </div>
                    </div>
                    ${this.renderBroadcastStudio(ownedStreamer)}
                </section>
                <section class="control-card">
                    <div class="panel-heading compact-heading">
                        <div>
                            <p class="eyebrow">OBS</p>
                            <h3>Use ingest key</h3>
                        </div>
                    </div>
                    <div class="obs-credentials-card">
                        <p class="form-hint">Reuse your BandLab-linked streamer in OBS or another RTMPS broadcaster whenever you want to go live.</p>
                        <div class="credential-block">
                            <span class="credential-label">Server</span>
                            <code>${escapeHtml(resolveObsServerValue(ownedStreamer))}</code>
                        </div>
                        <div class="credential-block">
                            <span class="credential-label">Stream key</span>
                            <div class="credential-row">
                                <code>${escapeHtml(revealedCredentials?.streamKeyValue ?? maskSecret(null))}</code>
                                ${canRevealObsCredentials ? `
                                <button type="button" class="ghost-button" data-action="toggle-obs-key-visibility">
                                    ${revealedCredentials ? "Hide key" : ownedStreamer.requiresReprovision ? "Get new key" : "Show key"}
                                </button>
                                ` : ""}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        `;
    }

    private renderBlockedAccountNotice(): string {
        return `
            <section class="panel blocked-account-panel">
                <div class="panel-heading">
                    <div>
                        <p class="eyebrow">Streaming access removed</p>
                        <h2>You can no longer livestream from this account.</h2>
                    </div>
                    <span class="status-pill">Blocked</span>
                </div>
                <p>
                    We removed livestream access on this account because of policy violations.
                    Please contact support if you think this was a mistake.
                </p>
            </section>
        `;
    }

    private renderAdminReviewPanel(): string {
        const adminReview = this.state.adminReview;
        if (!adminReview) {
            return `
                <section class="panel wide-panel admin-panel">
                    <div class="panel-heading">
                        <div>
                            <p class="eyebrow">Admin</p>
                            <h2>Moderation review</h2>
                        </div>
                    </div>
                    <div class="empty-card">Loading moderation review data…</div>
                </section>
            `;
        }

        return `
            <section class="panel wide-panel admin-panel">
                <div class="panel-heading">
                    <div>
                        <p class="eyebrow">Admin</p>
                        <h2>Moderation review</h2>
                    </div>
                </div>
                <div class="admin-review-grid">
                    <section class="admin-review-column">
                        <h3>Account actions</h3>
                        ${adminReview.streamers.length
        ? adminReview.streamers.map((streamer) => `
                                <article class="admin-review-card">
                                    <div>
                                        <strong>${escapeHtml(streamer.displayName)}</strong>
                                        <p>@${escapeHtml(streamer.handle)}</p>
                                        <p>${escapeHtml(resolveAdminStreamerSummary(streamer))}</p>
                                    </div>
                                    <button
                                        type="button"
                                        class="ghost-button"
                                        data-action="admin-restore-streamer-access"
                                        data-streamer-id="${streamer.streamerId}"
                                    >
                                        Restore access
                                    </button>
                                </article>
                            `).join("")
        : `<div class="empty-card">No warned or blocked accounts right now.</div>`}
                    </section>
                    <section class="admin-review-column">
                        <h3>Flagged sessions</h3>
                        ${adminReview.sessions.length
        ? adminReview.sessions.map((session) => `
                                <article class="admin-review-card">
                                    <div>
                                        <strong>${escapeHtml(resolveStreamerName(session.streamerId, this.state.streamers))}</strong>
                                        <p>${escapeHtml(formatDateTime(session.startedAt))}</p>
                                        <p>${escapeHtml(resolveAdminSessionSummary(session))}</p>
                                    </div>
                                    <div class="admin-review-actions">
                                        <button
                                            type="button"
                                            class="ghost-button"
                                            data-action="admin-review-session"
                                            data-streamer-id="${session.streamerId}"
                                            data-playback-url="${escapeHtmlAttribute(resolveSessionPlaybackUrl(session) ?? "")}"
                                            data-review-seek-seconds="${session.reviewOffsetSeconds ?? ""}"
                                            ${resolveSessionPlaybackUrl(session) ? "" : "disabled"}
                                        >
                                            Review
                                        </button>
                                        <button
                                            type="button"
                                            class="ghost-button"
                                            data-action="admin-restore-session"
                                            data-session-id="${session.sessionId}"
                                        >
                                            Restore
                                        </button>
                                        ${session.recordingStatus === "available" && !session.recordingRemoved ? `
                                            <button
                                                type="button"
                                                class="ghost-button danger"
                                                data-action="admin-remove-recording"
                                                data-session-id="${session.sessionId}"
                                            >
                                                Remove recording
                                            </button>
                                        ` : ""}
                                    </div>
                                </article>
                            `).join("")
        : `<div class="empty-card">No blocked sessions waiting for review.</div>`}
                    </section>
                </div>
            </section>
        `;
    }

    private renderRosterList(streamers: Streamer[]): string {
        return `
            <div class="list-stack roster-list">
                ${streamers.length
        ? streamers.map((streamer) => `
                        <button
                            type="button"
                            class="list-card roster-card ${streamer.streamerId === this.state.selectedStreamerId ? "selected" : ""}"
                            data-action="select-streamer"
                            data-streamer-id="${streamer.streamerId}"
                        >
                            <div class="roster-card-main">
                                <span class="status-dot ${escapeHtmlAttribute(getStreamerStatusTone(streamer, this.state.liveSessions))}" aria-hidden="true"></span>
                                <div class="roster-card-copy">
                                    <span class="list-card-title">${escapeHtml(streamer.displayName)}</span>
                                    <span class="list-card-subtitle">@${escapeHtml(streamer.handle)}</span>
                                </div>
                            </div>
                            <span class="status-pill ${resolveStreamerStatus(streamer, this.state.liveSessions) === "live" ? "live" : ""}">${escapeHtml(formatStatus(resolveStreamerStatus(streamer, this.state.liveSessions)))}</span>
                        </button>
                    `).join("")
        : `<div class="empty-card">No streamers yet. Create the first internal test streamer here.</div>`}
            </div>
        `;
    }

    private renderBroadcastStudio(selectedStreamer: Streamer | null): string {
        if (!this.state.webBroadcastSupported) {
            return `<div class="empty-card">This browser does not support the IVS Web Broadcast SDK requirements.</div>`;
        }

        if (!selectedStreamer) {
            return `<div class="empty-card">Choose a streamer from the roster to open the browser broadcast studio.</div>`;
        }

        const broadcastBlockState = resolveEffectiveBroadcastBlockState(
            selectedStreamer,
            this.state.webBroadcastEnding && selectedStreamer.streamerId === this.state.ownedStreamerId,
        );
        if (!selectedStreamer.ingestEndpoint || (!selectedStreamer.canBroadcast && !this.state.webBroadcastActive && !broadcastBlockState.message)) {
            return `<div class="empty-card">${escapeHtml(resolveBroadcastAvailabilityMessage(selectedStreamer))}</div>`;
        }

        const startButtonLabel = resolveStartBroadcastButtonLabel({
            webBroadcastActive: this.state.webBroadcastActive,
            webBroadcastStarting: this.state.webBroadcastStarting,
            broadcastBlockState,
            videoDeviceCount: this.state.webBroadcastVideoDevices.length,
            audioDeviceCount: this.state.webBroadcastAudioDevices.length,
        });
        const startButtonDisabled = resolveStartBroadcastButtonDisabled({
            webBroadcastPreparing: this.state.webBroadcastPreparing,
            webBroadcastStarting: this.state.webBroadcastStarting,
            webBroadcastActive: this.state.webBroadcastActive,
            broadcastBlockState,
            videoDeviceCount: this.state.webBroadcastVideoDevices.length,
            audioDeviceCount: this.state.webBroadcastAudioDevices.length,
        });
        return `
            <div class="broadcast-studio">
                <p class="form-hint">
                    Use your camera and microphone to publish directly to ${escapeHtml(selectedStreamer.displayName)} without OBS.
                    Chrome works best for this POC.
                </p>
                ${this.state.webBroadcastErrorMessage ? `<div class="inline-error">${escapeHtml(this.state.webBroadcastErrorMessage)}</div>` : ""}
                ${broadcastBlockState.message && !this.state.webBroadcastActive ? `<div class="inline-warning" data-role="broadcast-block-message">${escapeHtml(broadcastBlockState.message)}</div>` : ""}
                <div class="broadcast-preview-card">
                    <canvas data-role="broadcast-preview" width="1280" height="720" aria-label="Browser broadcast preview"></canvas>
                </div>
                <div class="broadcast-device-grid">
                    <label>
                        Camera
                        <select
                            data-role="broadcast-video-device"
                            ${this.state.webBroadcastVideoDevices.length ? "" : "disabled"}
                            ${this.state.webBroadcastActive ? "disabled" : ""}
                        >
                            ${this.state.webBroadcastVideoDevices.length
        ? this.state.webBroadcastVideoDevices.map((device) => `
                                    <option
                                        value="${escapeHtmlAttribute(device.deviceId)}"
                                        ${device.deviceId === this.state.selectedWebBroadcastVideoDeviceId ? "selected" : ""}
                                    >
                                        ${escapeHtml(device.label)}
                                    </option>
                                `).join("")
        : `<option value="">Enable camera first</option>`}
                        </select>
                    </label>
                    <label>
                        Microphone
                        <select
                            data-role="broadcast-audio-device"
                            ${this.state.webBroadcastAudioDevices.length ? "" : "disabled"}
                            ${this.state.webBroadcastActive ? "disabled" : ""}
                        >
                            ${this.state.webBroadcastAudioDevices.length
        ? this.state.webBroadcastAudioDevices.map((device) => `
                                    <option
                                        value="${escapeHtmlAttribute(device.deviceId)}"
                                        ${device.deviceId === this.state.selectedWebBroadcastAudioDeviceId ? "selected" : ""}
                                    >
                                        ${escapeHtml(device.label)}
                                    </option>
                                `).join("")
        : `<option value="">Enable microphone first</option>`}
                        </select>
                    </label>
                </div>
                <div class="broadcast-actions">
                    <button
                        type="button"
                        class="ghost-button"
                        data-action="prepare-broadcast-studio"
                        ${this.state.webBroadcastPreparing || this.state.webBroadcastActive ? "disabled" : ""}
                    >
                        ${this.state.webBroadcastVideoDevices.length || this.state.webBroadcastAudioDevices.length ? "Refresh preview" : "Enable camera + mic"}
                    </button>
                    <button
                        type="button"
                        class="primary-button"
                        data-role="start-browser-broadcast-button"
                        data-action="${this.state.webBroadcastActive ? "stop-browser-broadcast" : "start-browser-broadcast"}"
                        ${startButtonDisabled ? "disabled" : ""}
                    >
                        ${escapeHtml(startButtonLabel)}
                    </button>
                </div>
            </div>
        `;
    }

    private bindEvents(): void {
        this.root.querySelectorAll<HTMLElement>("[data-action]").forEach((element) => {
            element.addEventListener("click", () => {
                const action = element.dataset.action;
                if (!action) {
                    return;
                }

                void this.handleAction(action, element.dataset);
            });
        });

        const videoDeviceSelect = this.root.querySelector<HTMLSelectElement>('[data-role="broadcast-video-device"]');
        videoDeviceSelect?.addEventListener("change", () => {
            this.updateState({
                selectedWebBroadcastVideoDeviceId: videoDeviceSelect.value || null,
            });
            void this.prepareWebBroadcastStudio();
        });

        const audioDeviceSelect = this.root.querySelector<HTMLSelectElement>('[data-role="broadcast-audio-device"]');
        audioDeviceSelect?.addEventListener("change", () => {
            this.updateState({
                selectedWebBroadcastAudioDeviceId: audioDeviceSelect.value || null,
            });
            void this.prepareWebBroadcastStudio();
        });
    }

    private bindPlayer(): void {
        const videoElement = this.root.querySelector<HTMLVideoElement>('[data-role="player"]');
        if (!videoElement) {
            if (this.detachPlayer) {
                this.detachPlayer();
                this.detachPlayer = null;
            }
            return;
        }

        const qualityControls = this.root.querySelector<HTMLElement>('[data-role="quality-controls"]');
        const playerControls = this.root.querySelector<HTMLElement>('[data-role="player-controls"]');
        const selectedStreamer = this.state.streamers.find((streamer) => streamer.streamerId === this.state.selectedStreamerId) ?? null;
        const selectedPlaybackSession = resolveSelectedPlaybackSession(selectedStreamer, this.state.selectedStreamerSessions, this.state.selectedPlaybackUrl);

        if (this.detachPlayer) {
            this.detachPlayer();
            this.detachPlayer = null;
        }

        this.detachPlayer = attachPlayback(
            videoElement,
            selectedPlaybackSession,
            qualityControls,
            playerControls,
            this.state.selectedPlaybackSeekSeconds,
        );
    }

    private bindBroadcastStudio(): void {
        const previewCanvas = this.root.querySelector<HTMLCanvasElement>('[data-role="broadcast-preview"]');
        this.webBroadcastController.attachPreviewCanvas(previewCanvas);
    }

    private async prepareWebBroadcastStudio(): Promise<void> {
        const ownedStreamer = this.state.streamers.find((streamer) => streamer.streamerId === this.state.ownedStreamerId) ?? null;
        if (!ownedStreamer?.canBroadcast || !ownedStreamer.ingestEndpoint) {
            return;
        }

        try {
            this.updateState({
                webBroadcastPreparing: true,
                webBroadcastErrorMessage: null,
            });

            const preparedStudio = await this.webBroadcastController.prepare({
                ingestEndpoint: ownedStreamer.ingestEndpoint,
            }, {
                videoDeviceId: this.state.selectedWebBroadcastVideoDeviceId,
                audioDeviceId: this.state.selectedWebBroadcastAudioDeviceId,
                onConnectionStateChange: (connectionState) => {
                    this.handleWebBroadcastConnectionStateChange(connectionState);
                },
                onActiveStateChange: (active) => {
                    this.handleWebBroadcastActiveStateChange(active);
                },
            });

            this.updateState({
                webBroadcastPreparing: false,
                webBroadcastVideoDevices: preparedStudio.videoDevices,
                webBroadcastAudioDevices: preparedStudio.audioDevices,
                selectedWebBroadcastVideoDeviceId: preparedStudio.selectedVideoDeviceId,
                selectedWebBroadcastAudioDeviceId: preparedStudio.selectedAudioDeviceId,
                webBroadcastErrorMessage: null,
                webBroadcastConnectionState: "ready",
            });
        } catch (error) {
            this.webBroadcastController.dispose();
            this.updateState({
                webBroadcastPreparing: false,
                webBroadcastActive: false,
                webBroadcastLiveSessionObserved: false,
                webBroadcastConnectionState: null,
                webBroadcastErrorMessage: getErrorMessage(error),
            });
        }
    }

    private async startWebBroadcast(): Promise<void> {
        if (!this.state.ownedStreamerId) {
            return;
        }

        try {
            const ownedStreamer = this.state.streamers.find((streamer) => streamer.streamerId === this.state.ownedStreamerId) ?? null;
            const membershipAccessMessage = resolveMembershipAccessMessage(ownedStreamer);
            if (membershipAccessMessage) {
                this.updateState({
                    webBroadcastErrorMessage: membershipAccessMessage,
                });
                return;
            }

            const broadcastBlockState = resolveEffectiveBroadcastBlockState(ownedStreamer, this.state.webBroadcastEnding);
            if (broadcastBlockState.message) {
                this.updateState({
                    webBroadcastErrorMessage: broadcastBlockState.message,
                });
                return;
            }

            if (!this.state.webBroadcastVideoDevices.length || !this.state.webBroadcastAudioDevices.length) {
                this.updateState({
                    webBroadcastErrorMessage: resolveMissingBroadcastDeviceMessage(
                        this.state.webBroadcastVideoDevices.length,
                        this.state.webBroadcastAudioDevices.length,
                    ),
                });
                return;
            }

            this.updateState({
                webBroadcastStarting: true,
                webBroadcastEnding: false,
                webBroadcastErrorMessage: null,
            });

            const credentials = await this.apiClient.getMyBroadcastCredentials();
            await this.webBroadcastController.start(credentials);

            this.updateState({
                webBroadcastStarting: false,
                webBroadcastActive: true,
                webBroadcastEnding: false,
                webBroadcastLiveSessionObserved: false,
                webBroadcastConnectionState: "connecting",
                selectedStreamerId: this.state.ownedStreamerId,
                selectedPlaybackUrl: credentials.playbackUrl ?? this.state.selectedPlaybackUrl,
            });
            this.startBroadcastStartupPolling();
        } catch (error) {
            if (this.handleUnauthorizedError(error)) {
                return;
            }

            this.updateState({
                webBroadcastStarting: false,
                webBroadcastActive: false,
                webBroadcastEnding: false,
                webBroadcastLiveSessionObserved: false,
                webBroadcastErrorMessage: getErrorMessage(error),
            });
        }
    }

    private stopWebBroadcast(): void {
        this.stopBroadcastStartupPolling();
        this.webBroadcastController.stop();
        this.updateState({
            webBroadcastActive: false,
            webBroadcastEnding: true,
            webBroadcastLiveSessionObserved: false,
            webBroadcastConnectionState: "ending",
        });
        void this.refreshDashboardData({ forceRender: true, silent: true });
    }

    private handleWebBroadcastConnectionStateChange(connectionState: string | null): void {
        const active = connectionState === "connected" || connectionState === "connecting";
        this.updateWebBroadcastRuntimeState(active, connectionState);
    }

    private handleWebBroadcastActiveStateChange(active: boolean): void {
        this.updateWebBroadcastRuntimeState(
            active,
            active ? this.state.webBroadcastConnectionState ?? "connected" : "disconnected",
        );
    }

    private updateWebBroadcastRuntimeState(active: boolean, connectionState: string | null): void {
        const wasActive = this.state.webBroadcastActive || this.state.webBroadcastStarting;
        const nextConnectionState = !active && this.state.webBroadcastEnding ? "ending" : connectionState;

        if (!active && wasActive) {
            this.stopBroadcastStartupPolling();
        }

        this.updateState({
            webBroadcastActive: active,
            webBroadcastStarting: active ? this.state.webBroadcastStarting : false,
            webBroadcastEnding: active ? false : wasActive || this.state.webBroadcastEnding,
            webBroadcastLiveSessionObserved: active ? this.state.webBroadcastLiveSessionObserved : false,
            webBroadcastConnectionState: nextConnectionState,
        }, {
            render: !active,
        });

        if (!active && wasActive) {
            void this.refreshDashboardData({ forceRender: true, silent: true });
        }
    }

    private async toggleObsKeyVisibility(): Promise<void> {
        const ownedStreamer = this.state.streamers.find((streamer) => streamer.streamerId === this.state.ownedStreamerId) ?? null;
        if (!ownedStreamer || !canRevealObsCredentialsForStreamer(ownedStreamer, this.state.webBroadcastEnding)) {
            this.updateState({
                revealedBroadcastCredentials: null,
            }, {
                render: false,
            });
            return;
        }

        if (this.state.revealedBroadcastCredentials?.streamerId === this.state.ownedStreamerId) {
            this.updateState({
                revealedBroadcastCredentials: null,
            });
            return;
        }

        try {
            this.updateState({
                errorMessage: null,
            }, {
                render: false,
            });

            const credentials = await this.apiClient.getMyBroadcastCredentials();
            this.updateState({
                revealedBroadcastCredentials: credentials,
            });
            await this.refreshDashboardData({ silent: true });
        } catch (error) {
            if (this.handleUnauthorizedError(error)) {
                return;
            }

            this.updateState({
                errorMessage: getErrorMessage(error),
            });
        }
    }

    private resetWebBroadcastStudio(): void {
        this.stopBroadcastStartupPolling();
        this.webBroadcastController.dispose();
        this.updateState({
            webBroadcastPreparing: false,
            webBroadcastStarting: false,
            webBroadcastActive: false,
            webBroadcastEnding: false,
            webBroadcastLiveSessionObserved: false,
            webBroadcastErrorMessage: null,
            webBroadcastConnectionState: null,
            webBroadcastVideoDevices: [],
            webBroadcastAudioDevices: [],
            selectedWebBroadcastVideoDeviceId: null,
            selectedWebBroadcastAudioDeviceId: null,
        }, {
            render: false,
        });
        this.stopBroadcastCountdownTicker();
    }

    private startBroadcastStartupPolling(): void {
        this.stopBroadcastStartupPolling();

        this.startupPollingHandle = window.setInterval(() => {
            void this.pollForSelectedStreamerLiveSession();
        }, 3000);
    }

    private stopBroadcastStartupPolling(): void {
        if (this.startupPollingHandle !== null) {
            window.clearInterval(this.startupPollingHandle);
            this.startupPollingHandle = null;
        }
    }

    private startDashboardPolling(): void {
        this.stopDashboardPolling();
        this.dashboardPollingHandle = window.setInterval(() => {
            void this.refreshDashboardData({ silent: true });
        }, 15000);
    }

    private stopDashboardPolling(): void {
        if (this.dashboardPollingHandle !== null) {
            window.clearInterval(this.dashboardPollingHandle);
            this.dashboardPollingHandle = null;
        }
    }

    private reconcileBroadcastCountdownTicker(): void {
        const ownedStreamer = this.getOwnedStreamer();
        const broadcastBlockState = resolveEffectiveBroadcastBlockState(ownedStreamer, this.state.webBroadcastEnding);
        const hasActiveTimedBlock = Boolean(broadcastBlockState.blockedUntil && broadcastBlockState.message);
        const shouldPollEndingState = this.state.webBroadcastEnding;

        if (!hasActiveTimedBlock && !shouldPollEndingState) {
            this.stopBroadcastCountdownTicker();
            return;
        }

        if (this.broadcastCountdownHandle !== null) {
            return;
        }

        this.broadcastCountdownHandle = window.setInterval(() => {
            void this.handleBroadcastCountdownTick();
        }, 1000);
    }

    private stopBroadcastCountdownTicker(): void {
        if (this.broadcastCountdownHandle !== null) {
            window.clearInterval(this.broadcastCountdownHandle);
            this.broadcastCountdownHandle = null;
        }
    }

    private async handleBroadcastCountdownTick(): Promise<void> {
        const ownedStreamer = this.getOwnedStreamer();
        const nowMs = Date.now();
        const broadcastBlockExpired = shouldRefreshExpiredBroadcastBlock(ownedStreamer, nowMs);
        const broadcastBlockState = resolveEffectiveBroadcastBlockState(ownedStreamer, this.state.webBroadcastEnding, nowMs);

        if (this.state.webBroadcastEnding) {
            await this.refreshBroadcastAvailabilityFromBackend();
            return;
        }

        if (broadcastBlockExpired) {
            this.stopBroadcastCountdownTicker();
            await this.refreshBroadcastAvailabilityFromBackend();
            return;
        }

        if (!broadcastBlockState.blockedUntil || !broadcastBlockState.message) {
            this.stopBroadcastCountdownTicker();
            return;
        }

        this.refreshBroadcastCountdownUi(broadcastBlockState);
    }

    private async refreshBroadcastAvailabilityFromBackend(): Promise<void> {
        if (this.broadcastBlockRefreshInFlight) {
            return;
        }

        this.broadcastBlockRefreshInFlight = true;
        try {
            await this.refreshDashboardData({ forceRender: true, silent: true });
        } finally {
            this.broadcastBlockRefreshInFlight = false;
        }
    }

    private refreshBroadcastCountdownUi(broadcastBlockState = resolveEffectiveBroadcastBlockState(this.getOwnedStreamer(), this.state.webBroadcastEnding)): void {
        if (!broadcastBlockState.message) {
            return;
        }

        this.root.querySelectorAll<HTMLElement>('[data-role="broadcast-block-message"]').forEach((element) => {
            element.textContent = broadcastBlockState.message ?? "";
        });

        const startButton = this.root.querySelector<HTMLButtonElement>('[data-role="start-browser-broadcast-button"]');
        if (!startButton || this.state.webBroadcastActive) {
            return;
        }

        startButton.textContent = resolveStartBroadcastButtonLabel({
            webBroadcastActive: false,
            webBroadcastStarting: this.state.webBroadcastStarting,
            broadcastBlockState,
            videoDeviceCount: this.state.webBroadcastVideoDevices.length,
            audioDeviceCount: this.state.webBroadcastAudioDevices.length,
        });
        startButton.disabled = resolveStartBroadcastButtonDisabled({
            webBroadcastPreparing: this.state.webBroadcastPreparing,
            webBroadcastStarting: this.state.webBroadcastStarting,
            webBroadcastActive: this.state.webBroadcastActive,
            broadcastBlockState,
            videoDeviceCount: this.state.webBroadcastVideoDevices.length,
            audioDeviceCount: this.state.webBroadcastAudioDevices.length,
        });
    }

    private getOwnedStreamer(): Streamer | null {
        return this.state.streamers.find((streamer) => streamer.streamerId === this.state.ownedStreamerId) ?? null;
    }

    private async pollForSelectedStreamerLiveSession(): Promise<void> {
        if (!this.state.ownedStreamerId) {
            this.stopBroadcastStartupPolling();
            return;
        }

        const ownedStreamerId = this.state.ownedStreamerId;

        try {
            const [liveSessions, selectedStreamerSessions] = await Promise.all([
                this.apiClient.listLiveSessions(),
                this.apiClient.getMyStreamerSessions(),
            ]);
            const liveSessionForSelectedStreamer = selectedStreamerSessions.find((session) => session.isLive)
                ?? liveSessions.find((session) => session.streamerId === ownedStreamerId)
                ?? null;

            this.updateState({
                liveSessions,
                selectedStreamerSessions,
                selectedStreamerId: ownedStreamerId,
                selectedPlaybackUrl: liveSessionForSelectedStreamer?.playbackUrl ?? this.state.selectedPlaybackUrl,
                webBroadcastLiveSessionObserved: Boolean(liveSessionForSelectedStreamer) || this.state.webBroadcastLiveSessionObserved,
                lastRefreshedAt: new Date().toISOString(),
            });

            if (liveSessionForSelectedStreamer) {
                this.stopBroadcastStartupPolling();
            }
        } catch {
            // Ignore temporary poll failures while IVS state catches up.
        }
    }

    private async handleAction(action: string, data: DOMStringMap): Promise<void> {
        switch (action) {
        case "sign-in":
            await this.authStore.startLogin();
            break;
        case "sign-out":
            this.resetWebBroadcastStudio();
            this.stopDashboardPolling();
            this.authStore.logout();
            break;
        case "refresh-dashboard":
            await this.refreshDashboardData();
            break;
        case "select-streamer":
            if (!data.streamerId) {
                return;
            }

            this.updateState({
                selectedStreamerId: data.streamerId,
                selectedStreamerSessions: [],
                selectedPlaybackUrl: null,
            });
            await this.refreshDashboardData({ selectedStreamerId: data.streamerId });
            break;
        case "toggle-obs-key-visibility":
            await this.toggleObsKeyVisibility();
            break;
        case "prepare-broadcast-studio":
            await this.prepareWebBroadcastStudio();
            break;
        case "start-browser-broadcast":
            await this.startWebBroadcast();
            break;
        case "stop-browser-broadcast":
            this.stopWebBroadcast();
            break;
        case "watch-playback":
            this.updateState({
                selectedPlaybackUrl: data.playbackUrl || null,
                selectedPlaybackSeekSeconds: null,
            });
            break;
        case "admin-review-session":
            this.updateState({
                selectedStreamerId: data.streamerId || null,
                selectedPlaybackUrl: data.playbackUrl || null,
                selectedPlaybackSeekSeconds: parseReviewSeekSeconds(data.reviewSeekSeconds),
            });
            await this.refreshDashboardData({
                selectedPlaybackSeekSeconds: parseReviewSeekSeconds(data.reviewSeekSeconds),
                selectedStreamerId: data.streamerId || null,
            });
            break;
        case "admin-restore-session":
            if (!data.sessionId) {
                return;
            }
            try {
                await this.apiClient.restoreSession(data.sessionId);
                await this.refreshDashboardData();
            } catch (error) {
                if (this.handleUnauthorizedError(error)) {
                    return;
                }

                this.updateState({
                    errorMessage: getErrorMessage(error),
                });
            }
            break;
        case "admin-remove-recording":
            if (!data.sessionId) {
                return;
            }

            if (!window.confirm("Remove this recording from playback for policy enforcement?")) {
                return;
            }
            try {
                await this.apiClient.removeRecordingForModeration(data.sessionId);
                await this.refreshDashboardData();
            } catch (error) {
                if (this.handleUnauthorizedError(error)) {
                    return;
                }

                this.updateState({
                    errorMessage: getErrorMessage(error),
                });
            }
            break;
        case "admin-restore-streamer-access":
            if (!data.streamerId) {
                return;
            }
            try {
                await this.apiClient.restoreStreamerAccess(data.streamerId);
                await this.refreshDashboardData();
            } catch (error) {
                if (this.handleUnauthorizedError(error)) {
                    return;
                }

                this.updateState({
                    errorMessage: getErrorMessage(error),
                });
            }
            break;
        case "delete-session":
            if (!data.sessionId) {
                return;
            }

            if (!window.confirm("Delete this session and its recording assets?")) {
                return;
            }

            try {
                await this.apiClient.deleteSession(data.sessionId);
                await this.refreshDashboardData();
            } catch (error) {
                if (this.handleUnauthorizedError(error)) {
                    return;
                }

                this.updateState({
                    errorMessage: getErrorMessage(error),
                });
            }
            break;
        default:
            break;
        }
    }

    private handleUnauthorizedError(error: unknown): boolean {
        if (!(error instanceof LivestreamingApiError) || error.statusCode !== 401) {
            return false;
        }

        this.resetWebBroadcastStudio();
        this.stopDashboardPolling();
        this.updateState({
            authSession: null,
            authCallbackProcessing: false,
            authCallbackError: null,
            apiHealthy: true,
            loading: false,
            errorMessage: null,
            streamers: [],
            ownedStreamerId: null,
            selectedStreamerId: null,
            selectedStreamerSessions: [],
            liveSessions: [],
            selectedPlaybackUrl: null,
            selectedPlaybackSeekSeconds: null,
            lastRefreshedAt: null,
            adminReview: null,
            revealedBroadcastCredentials: null,
            webBroadcastVideoDevices: [],
            webBroadcastAudioDevices: [],
            selectedWebBroadcastVideoDeviceId: null,
            selectedWebBroadcastAudioDeviceId: null,
        });
        return true;
    }

    private shouldAvoidFullDashboardRerender(): boolean {
        return Boolean(this.state.selectedPlaybackUrl || this.state.webBroadcastActive || this.state.webBroadcastPreparing || this.state.webBroadcastStarting);
    }

    private refreshPassiveDashboardUi(): void {
        const apiHealthChip = this.root.querySelector<HTMLElement>('[data-role="api-health-chip"]');
        if (apiHealthChip) {
            apiHealthChip.textContent = this.state.apiHealthy ? "API healthy" : "API unavailable";
            apiHealthChip.classList.toggle("healthy", this.state.apiHealthy);
            apiHealthChip.classList.toggle("warning", !this.state.apiHealthy);
        }

        this.updateTextContent('[data-role="streamers-count"]', String(this.state.streamers.length));
        this.updateTextContent('[data-role="live-count"]', String(this.state.liveSessions.length));
        this.updateTextContent('[data-role="recordings-count"]', String(this.state.selectedStreamerSessions.filter((session) => session.playbackKind === "recording").length));
        this.updateTextContent('[data-role="last-refresh"]', formatDateTime(this.state.lastRefreshedAt));

        const rosterList = this.root.querySelector<HTMLElement>('[data-role="roster-list"]');
        if (rosterList) {
            rosterList.innerHTML = this.renderRosterList(sortStreamers(this.state.streamers, this.state.liveSessions));
            rosterList.querySelectorAll<HTMLElement>("[data-action]").forEach((element) => {
                element.addEventListener("click", () => {
                    const action = element.dataset.action;
                    if (!action) {
                        return;
                    }

                    void this.handleAction(action, element.dataset);
                });
            });
        }

        this.refreshBroadcastCountdownUi();
    }

    private updateTextContent(selector: string, value: string): void {
        const element = this.root.querySelector<HTMLElement>(selector);
        if (element) {
            element.textContent = value;
        }
    }

}

function renderBrand(): string {
    return `
        <svg width="126" height="32" viewBox="0 0 126 32" aria-label="BandLab" role="img" fill="none">
            <path fill="#f12c18" d="M10.9 21.6c-1.6 0-2.6-1-2.6-2.3 0-1.7 1.9-3.3 3.9-3.7L7.5 4h8.2l1.5 2.9h-5.8l3.4 8.3c.3.7.5 1.4.5 2.1 0 2.5-2.5 4.3-4.4 4.3ZM19.3 10l3.6 6.1a8.8 8.8 0 0 1 1.1 4c0 4.6-3.5 7.8-8.6 7.8H8.6c-5 0-8.6-3.2-8.6-7.7a8.8 8.8 0 0 1 1.1-4L4.7 10H8l-4.2 7.2a6 6 0 0 0-.8 3C3 23 5 25 8.7 25h6.6c3.7 0 5.7-2 5.7-4.9a6 6 0 0 0-.8-2.9L16 10.1h3.3Z"></path>
            <path fill="#fff" d="M35.7 8.2c3 0 5.2 1.7 5.2 4.5 0 1.5-.7 2.7-2 3.4a4 4 0 0 1 3 4c0 3-2.2 4.9-5.8 4.9H30V8.2h5.7Zm-2.9 14.3H36c1.8 0 3-.9 3-2.5 0-1.5-1.2-2.5-3-2.5h-3.2v5Zm0-7.4h2.7c1.5 0 2.6-.8 2.6-2.2s-1-2.2-2.6-2.2h-2.7V15Zm23.1-1.9V25h-2.7v-1.7a4.9 4.9 0 0 1-4 2c-3.2 0-5.7-2.7-5.7-6.2S46 13 49.2 13c1.6 0 3.1.7 4 2v-1.7h2.7Zm-9.7 6c0 2 1.6 3.6 3.6 3.6s3.5-1.6 3.5-3.7-1.5-3.6-3.5-3.6-3.6 1.6-3.6 3.6ZM61.5 25h-2.8V13.2h2.8v1.6a4.1 4.1 0 0 1 3.6-1.9c2.8 0 4.5 2 4.5 5V25h-2.8v-6.5c0-1.9-1-3-2.6-3-1.7 0-2.8 1.3-2.8 3.3V25Zm19.9-1.7a5 5 0 0 1-4 2c-3.2 0-5.7-2.7-5.7-6.2 0-3.4 2.5-6.2 5.7-6.2 1.6 0 3.1.7 4 2V8.1H84V25h-2.7v-1.7Zm-7-4.2c0 2 1.6 3.7 3.6 3.7s3.5-1.6 3.5-3.7-1.5-3.6-3.5-3.6-3.6 1.6-3.6 3.6ZM90.2 8.2v14.1h6.4V25h-9.2V8.2h2.8Zm19.4 5V25h-2.8v-1.7a4.9 4.9 0 0 1-4 2c-3.2 0-5.7-2.7-5.7-6.2s2.5-6.2 5.7-6.2c1.7 0 3.2.7 4 2v-1.7h2.8Zm-9.7 6c0 2 1.5 3.6 3.5 3.6s3.5-1.6 3.5-3.7-1.5-3.6-3.5-3.6S100 17 100 19Zm12.4 5.8V8.2h2.8v6.7a5 5 0 0 1 4-2c3.2 0 5.7 2.7 5.7 6.2s-2.5 6.2-5.7 6.2c-1.7 0-3.2-.7-4-2V25h-2.8Zm2.7-5.9c0 2 1.5 3.7 3.5 3.7s3.5-1.6 3.5-3.7-1.5-3.6-3.5-3.6S115 17 115 19Z"></path>
        </svg>
    `;
}

function renderUserChip(authSession: BandLabAuthSession | null): string {
    const displayName = authSession?.displayName ?? "Internal user";
    const email = authSession?.email ?? "";
    const pictureUrl = authSession?.pictureUrl ?? null;

    return `
        <div class="user-chip">
            ${pictureUrl
        ? `<img class="user-chip-avatar" src="${escapeHtmlAttribute(pictureUrl)}" alt="${escapeHtmlAttribute(displayName)} profile picture" />`
        : `<div class="user-chip-avatar fallback">${escapeHtml(displayName.slice(0, 1).toUpperCase())}</div>`}
            <div class="user-chip-copy">
                <span>${escapeHtml(displayName)}</span>
                <small>${escapeHtml(email)}</small>
            </div>
        </div>
    `;
}

function resolveSelectedStreamerId(streamers: Streamer[], selectedStreamerId: string | null, fallbackStreamerId?: string | null): string | null {
    if (selectedStreamerId && streamers.some((streamer) => streamer.streamerId === selectedStreamerId)) {
        return selectedStreamerId;
    }

    if (fallbackStreamerId && streamers.some((streamer) => streamer.streamerId === fallbackStreamerId)) {
        return fallbackStreamerId;
    }

    return streamers[0]?.streamerId ?? null;
}

function sortStreamers(streamers: Streamer[], liveSessions: Session[]): Streamer[] {
    return [...streamers].sort((left, right) => {
        const leftStatus = resolveStreamerStatus(left, liveSessions);
        const rightStatus = resolveStreamerStatus(right, liveSessions);

        if (leftStatus === "live" && rightStatus !== "live") {
            return -1;
        }

        if (rightStatus === "live" && leftStatus !== "live") {
            return 1;
        }

        return left.displayName.localeCompare(right.displayName);
    });
}

function mergeOwnedStreamer(streamers: Streamer[], ownedStreamer: Streamer): Streamer[] {
    const existingIndex = streamers.findIndex((streamer) => streamer.streamerId === ownedStreamer.streamerId);
    if (existingIndex === -1) {
        return [...streamers, ownedStreamer];
    }

    return streamers.map((streamer, index) => index === existingIndex ? ownedStreamer : streamer);
}

function canDeleteOwnedSession(session: Session, ownedStreamerId: string | null): boolean {
    return session.streamerId === ownedStreamerId && session.canDelete;
}

export function resolvePlaybackUrl(input: {
    existing: string | null;
    liveSessions: Session[];
    streamerSessions: Session[];
    allowGlobalLiveFallback?: boolean;
}): string | null {
    const streamerPlayableSessions = input.streamerSessions.filter((session) => session.canPlay);
    const livePlayableSessions = input.liveSessions.filter((session) => session.canPlay);
    const playableSessions = [
        ...streamerPlayableSessions,
        ...(input.allowGlobalLiveFallback ? livePlayableSessions : []),
    ];
    const candidateUrls = [
        playableSessions.some((session) => resolveSessionPlaybackUrl(session) === input.existing) ? input.existing : null,
        resolveSessionPlaybackUrl(streamerPlayableSessions[0] ?? null),
        input.allowGlobalLiveFallback ? resolveSessionPlaybackUrl(livePlayableSessions[0] ?? null) : null,
    ];

    return candidateUrls.find((value) => Boolean(value)) ?? null;
}

export function resolveWebBroadcastSessionReconciliation(input: {
    webBroadcastActive: boolean;
    webBroadcastStarting: boolean;
    liveSessionObserved: boolean;
    ownedStreamerId: string | null;
    liveSessions: Session[];
}): {
    nextLiveSessionObserved: boolean;
    stoppedByBackend: boolean;
} {
    const broadcastInProgress = input.webBroadcastActive || input.webBroadcastStarting;

    if (!broadcastInProgress) {
        return {
            nextLiveSessionObserved: false,
            stoppedByBackend: false,
        };
    }

    const ownedStreamerIsLive = Boolean(input.ownedStreamerId)
        && input.liveSessions.some((session) => session.streamerId === input.ownedStreamerId && session.isLive);

    if (ownedStreamerIsLive) {
        return {
            nextLiveSessionObserved: true,
            stoppedByBackend: false,
        };
    }

    if (input.liveSessionObserved) {
        return {
            nextLiveSessionObserved: false,
            stoppedByBackend: true,
        };
    }

    return {
        nextLiveSessionObserved: false,
        stoppedByBackend: false,
    };
}

export function resolveStreamLimitCooldownState(
    streamer: Pick<Streamer, "streamLimitBlockedUntil"> | null,
    nowMs = Date.now(),
): {
    blockedUntil: string | null;
    remainingSeconds: number | null;
    message: string | null;
} {
    const blockedUntil = streamer?.streamLimitBlockedUntil ?? null;
    if (!blockedUntil) {
        return {
            blockedUntil: null,
            remainingSeconds: null,
            message: null,
        };
    }

    const blockedUntilMs = Date.parse(blockedUntil);
    if (Number.isNaN(blockedUntilMs) || blockedUntilMs <= nowMs) {
        return {
            blockedUntil: null,
            remainingSeconds: null,
            message: null,
        };
    }

    const remainingSeconds = Math.max(1, Math.ceil((blockedUntilMs - nowMs) / 1000));
    return {
        blockedUntil,
        remainingSeconds,
        message: `You can start another livestream in ${formatDuration(remainingSeconds)}. The previous stream hit the session limit, so immediate retries are paused.`,
    };
}

export function resolveBroadcastBlockState(
    streamer: Pick<Streamer, "broadcastBlockReason" | "nextBroadcastAllowedAt" | "streamLimitBlockedUntil"> | null,
    nowMs = Date.now(),
): {
    reason: Streamer["broadcastBlockReason"];
    blockedUntil: string | null;
    remainingSeconds: number | null;
    message: string | null;
} {
    const reason = streamer?.broadcastBlockReason ?? null;
    if (!streamer || !reason) {
        return {
            reason: null,
            blockedUntil: null,
            remainingSeconds: null,
            message: null,
        };
    }

    if (reason === "duration_limit_cooldown") {
        const cooldown = resolveStreamLimitCooldownState(streamer, nowMs);
        return {
            reason,
            blockedUntil: cooldown.blockedUntil,
            remainingSeconds: cooldown.remainingSeconds,
            message: cooldown.message,
        };
    }

    if (reason === "restart_wait") {
        return resolveTimedBroadcastBlockState({
            reason,
            blockedUntil: streamer.nextBroadcastAllowedAt,
            nowMs,
            message: remainingSeconds => `You can start another livestream in ${formatDuration(remainingSeconds)}. We are finishing the previous session so the next start gets its own recording.`,
        });
    }

    if (reason === "active_session") {
        return {
            reason,
            blockedUntil: null,
            remainingSeconds: null,
            message: "This streamer is already live. Stop the current broadcast before starting another one.",
        };
    }

    return {
        reason,
        blockedUntil: null,
        remainingSeconds: null,
        message: null,
    };
}

export function resolveEffectiveBroadcastBlockState(
    streamer: Pick<Streamer, "broadcastBlockReason" | "nextBroadcastAllowedAt" | "streamLimitBlockedUntil"> | null,
    webBroadcastEnding: boolean,
    nowMs = Date.now(),
): ReturnType<typeof resolveBroadcastBlockState> {
    const broadcastBlockState = resolveBroadcastBlockState(streamer, nowMs);
    if (!webBroadcastEnding || (broadcastBlockState.reason !== null && broadcastBlockState.reason !== "active_session")) {
        return broadcastBlockState;
    }

    return {
        reason: "active_session",
        blockedUntil: null,
        remainingSeconds: null,
        message: "We are ending the previous broadcast. Start will unlock once the backend confirms the next available start time.",
    };
}

export function shouldRefreshExpiredBroadcastBlock(
    streamer: Pick<Streamer, "broadcastBlockReason" | "nextBroadcastAllowedAt" | "streamLimitBlockedUntil"> | null,
    nowMs = Date.now(),
): boolean {
    const reason = streamer?.broadcastBlockReason ?? null;
    if (!streamer) {
        return false;
    }

    if (reason === "restart_wait") {
        return hasElapsedTimestamp(streamer.nextBroadcastAllowedAt, nowMs);
    }

    if (reason === "duration_limit_cooldown") {
        return hasElapsedTimestamp(streamer.streamLimitBlockedUntil, nowMs);
    }

    return false;
}

function hasElapsedTimestamp(timestamp: string | null, nowMs: number): boolean {
    if (!timestamp) {
        return false;
    }

    const timestampMs = Date.parse(timestamp);
    return !Number.isNaN(timestampMs) && timestampMs <= nowMs;
}

function resolveTimedBroadcastBlockState(input: {
    reason: Exclude<Streamer["broadcastBlockReason"], null>;
    blockedUntil: string | null;
    nowMs: number;
    message: (remainingSeconds: number) => string;
}): {
    reason: Streamer["broadcastBlockReason"];
    blockedUntil: string | null;
    remainingSeconds: number | null;
    message: string | null;
} {
    if (!input.blockedUntil) {
        return {
            reason: input.reason,
            blockedUntil: null,
            remainingSeconds: null,
            message: null,
        };
    }

    const blockedUntilMs = Date.parse(input.blockedUntil);
    if (Number.isNaN(blockedUntilMs) || blockedUntilMs <= input.nowMs) {
        return {
            reason: input.reason,
            blockedUntil: null,
            remainingSeconds: null,
            message: null,
        };
    }

    const remainingSeconds = Math.max(1, Math.ceil((blockedUntilMs - input.nowMs) / 1000));
    return {
        reason: input.reason,
        blockedUntil: input.blockedUntil,
        remainingSeconds,
        message: input.message(remainingSeconds),
    };
}

export function resolveStartBroadcastButtonLabel(input: {
    webBroadcastActive: boolean;
    webBroadcastStarting: boolean;
    broadcastBlockState: ReturnType<typeof resolveBroadcastBlockState>;
    videoDeviceCount: number;
    audioDeviceCount: number;
}): string {
    if (input.webBroadcastActive) {
        return "Stop broadcast";
    }

    if (input.webBroadcastStarting) {
        return "Starting...";
    }

    if (input.broadcastBlockState.reason === "active_session" && input.broadcastBlockState.message?.startsWith("We are ending")) {
        return "Ending...";
    }

    if (
        (input.broadcastBlockState.reason === "restart_wait" || input.broadcastBlockState.reason === "duration_limit_cooldown")
        && input.broadcastBlockState.remainingSeconds != null
    ) {
        return `Start in ${formatDuration(input.broadcastBlockState.remainingSeconds)}`;
    }

    if (!input.videoDeviceCount || !input.audioDeviceCount) {
        return resolveMissingBroadcastDeviceMessage(input.videoDeviceCount, input.audioDeviceCount);
    }

    return "Start broadcast";
}

export function resolveStartBroadcastButtonDisabled(input: {
    webBroadcastPreparing: boolean;
    webBroadcastStarting: boolean;
    webBroadcastActive: boolean;
    broadcastBlockState: ReturnType<typeof resolveBroadcastBlockState>;
    videoDeviceCount: number;
    audioDeviceCount: number;
}): boolean {
    return input.webBroadcastPreparing
        || input.webBroadcastStarting
        || (!input.webBroadcastActive && Boolean(input.broadcastBlockState.message))
        || (!input.webBroadcastActive && (!input.videoDeviceCount || !input.audioDeviceCount));
}

function resolveMissingBroadcastDeviceMessage(videoDeviceCount: number, audioDeviceCount: number): string {
    if (!videoDeviceCount && !audioDeviceCount) {
        return "Enable camera + mic first";
    }

    if (!videoDeviceCount) {
        return "Enable camera first";
    }

    return "Enable microphone first";
}

export function resolveSelectedPlaybackSession(selectedStreamer: Streamer | null, streamerSessions: Session[], selectedPlaybackUrl: string | null): Session | null {
    if (!selectedPlaybackUrl) {
        return null;
    }

    const matchedSession = streamerSessions
        .find((session) => resolveSessionPlaybackUrl(session) === selectedPlaybackUrl) ?? null;

    if (matchedSession) {
        return matchedSession;
    }

    if (selectedStreamer?.playbackUrl === selectedPlaybackUrl) {
        return buildSyntheticLiveSession(selectedStreamer);
    }

    return null;
}

function resolveStreamerName(streamerId: string, streamers: Streamer[]): string {
    return streamers.find((streamer) => streamer.streamerId === streamerId)?.displayName ?? streamerId;
}

function resolveStreamerStatus(streamer: Streamer, liveSessions: Session[]): string {
    if (liveSessions.some((session) => session.streamerId === streamer.streamerId)) {
        return "live";
    }

    if (streamer.broadcastSetupReady) {
        return "offline";
    }

    return streamer.status;
}

function getStreamerStatusTone(streamer: Streamer, liveSessions: Session[]): string {
    return resolveStreamerStatus(streamer, liveSessions) === "live" ? "live" : "offline";
}

function resolvePlaybackTitle(session: Session, selectedStreamer: Streamer | null, streamers: Streamer[]): string {
    if (selectedStreamer?.streamerId === session.streamerId) {
        return `${selectedStreamer.displayName} playback`;
    }

    return `${resolveStreamerName(session.streamerId, streamers)} playback`;
}

function resolvePlaybackSubtitle(session: Session): string {
    if (session.moderationStatus === "blocked") {
        return resolveBlockedSessionSubtitle(session);
    }

    if (session.isLive) {
        if (session.recordingStatus === "recording") {
            return "Live now • Stream in progress • Recording in progress";
        }

        if (session.recordingStatus === "not_started") {
            return "Live now • Stream in progress";
        }

        return "Live now • Stream in progress";
    }

    if (session.recordingExpired) {
        return `Recording expired • ${formatDuration(session.durationSeconds)}`;
    }

    if (session.playbackKind === "recording") {
        return `Recording available • ${formatDuration(session.durationSeconds)}`;
    }

    if (session.recordingStatus === "failed") {
        return `Ended • Recording failed • ${formatDuration(session.durationSeconds)}`;
    }

    if (session.recordingStatus === "not_started" && session.endReason === "duration_limit") {
        return `Ended • Duration limit • ${formatDuration(session.durationSeconds)}`;
    }

    if (session.recordingStatus === "not_started") {
        return `Ended • No recording • ${formatDuration(session.durationSeconds)}`;
    }

    return `Ended • ${formatStatus(session.recordingStatus)} • ${formatDuration(session.durationSeconds)}`;
}

function resolveSessionCardSubtitle(session: Session): string {
    if (session.moderationStatus === "blocked") {
        return resolveBlockedSessionSubtitle(session);
    }

    if (session.isLive) {
        if (session.recordingStatus === "recording") {
            return "Live now • Recording in progress";
        }

        if (session.recordingStatus === "not_started") {
            return "Live now • Stream in progress";
        }

        return "Live now • Stream in progress";
    }

    if (session.recordingExpired) {
        return `Recording expired • ${formatDuration(session.durationSeconds)}`;
    }

    if (session.playbackKind === "recording") {
        return `Recording available • ${formatDuration(session.durationSeconds)}`;
    }

    if (session.recordingStatus === "failed") {
        return `Recording failed • ${formatDuration(session.durationSeconds)}`;
    }

    if (session.recordingStatus === "not_started" && session.endReason === "duration_limit") {
        return `Duration limit • ${formatDuration(session.durationSeconds)}`;
    }

    if (session.recordingStatus === "not_started") {
        return `No recording • ${formatDuration(session.durationSeconds)}`;
    }

    return `${formatStatus(session.recordingStatus)} • ${formatDuration(session.durationSeconds)}`;
}

function resolveSessionPlaybackUrl(session: Session | null): string | null {
    if (!session) {
        return null;
    }

    if (session.playbackKind === "live" && session.playbackUrl) {
        return session.playbackUrl;
    }

    if (session.playbackKind === "recording" && session.recordingPlaybackUrl) {
        return session.recordingPlaybackUrl;
    }

    return null;
}

function buildSyntheticLiveSession(streamer: Streamer): Session {
    const timestamp = streamer.updatedAt || streamer.createdAt;

    return {
        sessionId: `preview-${streamer.streamerId}`,
        streamerId: streamer.streamerId,
        playbackUrl: streamer.playbackUrl,
        recordingPlaybackUrl: null,
        recordingThumbnailUrl: null,
        recordingRemoved: false,
        status: "live",
        recordingStatus: "recording",
        moderationStatus: "clear",
        moderationReason: null,
        reviewOffsetSeconds: null,
        isLive: true,
        canPlay: true,
        canDelete: false,
        playbackKind: "live",
        recordingDurationMs: null,
        recordingRetentionDays: null,
        recordingExpiresAt: null,
        recordingExpired: false,
        startedAt: timestamp,
        endedAt: null,
        durationSeconds: null,
        endReason: null,
        createdAt: timestamp,
        updatedAt: timestamp,
    };
}

function resolveBlockedSessionSubtitle(session: Session): string {
    if (session.recordingRemoved) {
        return "Blocked • Recording removed";
    }

    if (session.isLive) {
        return "Blocked • Stream stopped";
    }

    return "Blocked • Policy violation";
}

function resolveAdminStreamerSummary(streamer: Streamer): string {
    if (streamer.moderationStatus === "blocked") {
        return `Blocked account • ${streamer.moderationStrikes} strike${streamer.moderationStrikes === 1 ? "" : "s"}`;
    }

    if (streamer.moderationStrikes > 0) {
        return `Warned account • ${streamer.moderationStrikes} strike${streamer.moderationStrikes === 1 ? "" : "s"}`;
    }

    return "Review available";
}

function resolveAdminSessionSummary(session: Session): string {
    const evidenceSummary = session.reviewOffsetSeconds != null
        ? `Review from ${formatDuration(session.reviewOffsetSeconds)}`
        : "Review from start";

    if (session.recordingRemoved) {
        return `Recording removed • ${evidenceSummary}`;
    }

    return `${resolveBlockedSessionSubtitle(session)} • ${evidenceSummary}`;
}

function parseReviewSeekSeconds(value: string | undefined): number | null {
    if (!value) {
        return null;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function renderStreamerModerationNotice(streamer: Streamer): string | null {
    if (streamer.moderationStatus === "blocked") {
        return null;
    }

    if (streamer.requiresReprovision && streamer.moderationStrikes > 0) {
        return "Your last stream was stopped. Get a new streaming setup before going live again. Another violation may remove livestream access.";
    }

    if (streamer.moderationStrikes > 0) {
        return "This account already has a livestream policy warning. Another violation may remove livestream access.";
    }

    return null;
}

function canRevealObsCredentialsForStreamer(streamer: Streamer, webBroadcastEnding = false): boolean {
    return streamer.moderationStatus !== "blocked"
        && !resolveMembershipAccessMessage(streamer)
        && (streamer.broadcastSetupReady || streamer.requiresReprovision)
        && (!streamer.broadcastBlockReason || (streamer.broadcastBlockReason === "setup_not_ready" && streamer.requiresReprovision))
        && !resolveEffectiveBroadcastBlockState(streamer, webBroadcastEnding).message;
}

function resolveObsServerValue(streamer: Streamer): string {
    if (streamer.moderationStatus === "blocked") {
        return "Streaming unavailable while this account is blocked.";
    }

    const membershipAccessMessage = resolveMembershipAccessMessage(streamer);
    if (membershipAccessMessage) {
        return membershipAccessMessage;
    }

    if (streamer.requiresReprovision) {
        return "Request a new streaming setup below.";
    }

    return streamer.ingestEndpoint ? `rtmps://${streamer.ingestEndpoint}:443/app/` : "Preparing new streaming setup…";
}

function resolveBroadcastAvailabilityMessage(streamer: Streamer): string {
    if (streamer.moderationStatus === "blocked") {
        return "You're no longer allowed to livestream on this account because of policy violations.";
    }

    const membershipAccessMessage = resolveMembershipAccessMessage(streamer);
    if (membershipAccessMessage) {
        return membershipAccessMessage;
    }

    const broadcastBlockState = resolveBroadcastBlockState(streamer);
    if (broadcastBlockState.message) {
        return broadcastBlockState.message;
    }

    if (streamer.requiresReprovision) {
        return "Your last stream was stopped. Get a new streaming setup before you start broadcasting again.";
    }

    return "This streamer is still being prepared for browser broadcasting.";
}

function resolveMembershipAccessMessage(streamer: Pick<Streamer, "membershipAccess"> | null): string | null {
    const access = streamer?.membershipAccess;
    if (!access?.enforcementEnabled || access.status === "available") {
        return null;
    }

    if (access.status === "verification_unavailable") {
        return "Livestreaming access cannot be verified right now. Please try again shortly.";
    }

    return "Livestreaming requires an active BandLab membership.";
}

function maskSecret(secret: string | null | undefined): string {
    if (!secret) {
        return "Hidden";
    }

    if (secret.length <= 8) {
        return "••••••••";
    }

    return `${secret.slice(0, 4)}••••••••${secret.slice(-4)}`;
}

function escapeHtml(value: string): string {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll("\"", "&quot;")
        .replaceAll("'", "&#39;");
}

function escapeHtmlAttribute(value: string): string {
    return escapeHtml(value);
}

function getErrorMessage(error: unknown): string {
    if (error instanceof LivestreamingApiError) {
        return error.message;
    }

    if (error instanceof Error) {
        return error.message;
    }

    return "Something went wrong";
}
